import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { supabase } from './supabase';
import { useAuth } from './auth';

type OrgCtx = { id: string; timezone: string; locale: string; currency: string; name: string; firstName: string; role: string; ready: boolean };
const Ctx = createContext<OrgCtx>({ id: '', timezone: 'UTC', locale: 'en', currency: 'EUR', name: '', firstName: '', role: '', ready: false });

export function OrgProvider({ children }: { children: ReactNode }) {
  const { session } = useAuth();
  const [state, setState] = useState<OrgCtx>({ id: '', timezone: 'UTC', locale: 'en', currency: 'EUR', name: '', firstName: '', role: '', ready: false });

  useEffect(() => {
    if (!session?.user) return;
    (async () => {
      const [org, prof, mem] = await Promise.all([
        supabase.from('organizations').select('id,name,timezone,default_locale,default_currency').is('deleted_at', null).limit(1).single(),
        supabase.from('user_profiles').select('display_name').eq('user_id', session.user.id).is('deleted_at', null).limit(1).maybeSingle(),
        supabase.from('memberships').select('role').eq('user_id', session.user.id).is('deleted_at', null).limit(1).maybeSingle(),
      ]);
      const name = (prof.data?.display_name ?? session.user.email ?? '').trim();
      const firstName = name.includes('@') ? name.split('@')[0] : (name.split(' ')[0] || name);
      setState({
        id: (org.data as any)?.id ?? '',
        timezone: (org.data as any)?.timezone ?? 'UTC',
        locale: (org.data as any)?.default_locale ?? 'en',
        currency: (org.data as any)?.default_currency ?? 'EUR',
        name: (org.data as any)?.name ?? '',
        firstName,
        role: (mem.data as any)?.role ?? '',
        ready: true,
      });
    })();
  }, [session?.user?.id]);

  return <Ctx.Provider value={state}>{children}</Ctx.Provider>;
}

export const useOrg = () => useContext(Ctx);
